import {
    ICellMetric,
    ICellMetricUOM,
    ICellMetricUOMConverters,
    ICubePerspective,
    ICubePerspectiveOptions,
    ICubeSource,
    ICubeSourceChangeEvent,
    ICubeSourceMetricChangeEvent,
    IDimensionCompositeKeys,
    IDimensionDistinctKeys,
    IKeyOptions,
    IMetricView
} from "./PA.HyperRectangle.IF";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { DimensionType, IHierarchy } from "../PA.Core/PA.Core.IF.Hierarchy";
import { BehaviorSubject } from "rxjs";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import * as _ from "lodash";
import {
    WB_CubeSourcePerspectiveRow,
    WB_CubeSourceRow,
    WB_MetricDefinitionRow,
    WB_MetricViewRow
} from "../PA.Core/PAdbSchemaRows";
import { CubeSourceMetaDataService } from "../PA.HyperRectangle.Services/CubeSourceMetaDataService";
import { CubeSourceCell } from "./CubeSourceCell";
import {
    ICubeProfile, ICubeProfileCreateOptions,
    ICubeSourceDataSource,
    ICubeSourceParameterChangeEvent,
    ICubeSourcePolicyHierarchyProperties,
    ICubeSourceWithDataSources,
    IDataSourceOnRowsChangedEvent,
    IDimensionPolicyOptions, IKeyFieldPolicyOptions,
    IKnownKeyOptions,
    ILoadOptions, IMetricPolicyOptions,
    IRowScope
} from "./CubeSource.IF";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { CustomerHierarchy } from "../PA.MD.Services/CustomerHierarchy";
import { ProductHierarchy } from "../PA.MD.Services/ProductHierarchy";
import { PeriodHierarchy } from "../PA.MD.Services/PeriodHierarchy";
import { DataSourceJSData } from "./DataSourceJSData";
import { Perspective } from "./Perspective";
import { IUOMConverter, IUOMConverterOptions, IUOMPolicyOptions } from "../PA.MD.Services/PA.MD.Services.IF";
import { ExecutionSerializer } from "../PA.Core/Basic/ExecutionSerializer";
import { Index } from "../PA.Core/Basic/Index";
import { UOMConverterService } from "../PA.MD.Services/UOMConverterService";
import { CubeSourceMetricDefinition } from "./CubeSourceMetricDefinition";
import { CellMetricUtils } from "./CellMetricUtils";
import { ProgressProviderMultiplexor } from "../PA.Core/Basic/ProgressMultiplexor";
import { CreateOptions } from "../PA.Core/lib/ESRecordResolver"
import { ExceptionMessage_restate } from '../PA.Core/Basic/MessageUtils'
import { esWB_CubeSource_Perspective } from "../PA.Core/PAdbSchema";
import { FilteredHierarchy } from '../PA.MD.Services/FilteredHierarchy'


export interface ICubeSourceCreateOptions {

    CubeSourceId?: string,
    WbCubeSourceId?: number,
    Parameters?: { [name: string]: (number | string)[] },
    Enabled?: boolean,

    dbContext: PAdbContext,

    DimensionPolicyOptions: IDimensionPolicyOptions,
    MetricPolicyOptions?: IMetricPolicyOptions,
    KeyFieldPolicyOptions?: IKeyFieldPolicyOptions,
    LoadOptions?: { [alias_id: string]: ILoadOptions },
    KnownKeyOptions?: { [alias_id: string]: IKnownKeyOptions },
    UOMPolicyOptions?: IUOMPolicyOptions
    UOMConverterOptions?: IUOMConverterOptions
    DataSourceOptions?: {
        [alias_id: string]: {
            RecordResolverOptions?: CreateOptions
        }
    },
}


export class CubeSource
    implements ICubeSource, ICubeProfile,
        ICubeSourceWithDataSources {
    private createOptions: ICubeSourceCreateOptions
    private cubeSourceLoadMessageProvider: MessageProvider;
    private cubeSourceLoadProgressProvider: ProgressProviderMultiplexor;
    private cubeSourceOnLoadCompleteProgressProvider: ProgressProvider;
    private cubeSourceDataRowSyncProgressProvider: ProgressProvider;
    private onCubeSourcePropertyChange: BehaviorSubject<ICubeSourceChangeEvent>;
    private onCubeSourceMetricChange: BehaviorSubject<ICubeSourceMetricChangeEvent>;

    private bCubeSourceLoaded: boolean;
    private onICubeSourceLoadComplete: Promise<void>;

    private cubeSourceRow: WB_CubeSourceRow
    private metricRows: WB_MetricDefinitionRow[]
    private metricViewRows: WB_MetricViewRow[]

    private dctParameters: { [name: string]: (number | string)[] }
    private onParameterChanged: BehaviorSubject<ICubeSourceParameterChangeEvent>
    private enabled: boolean

    private dctHierarchy: { [dimType: string]: IHierarchy }
    private dctHierarchyProps: { [dimType: string]: ICubeSourcePolicyHierarchyProperties }
    private dctMetrics_DataSource: { [id: string]: ICellMetric }
    private dctMetricCellUOMs_Base: { [id: string]: ICellMetricUOM }
    private dctMetricConverters: { [converterId: string]: IUOMConverter }
    private dctMetrics_Metrics: { [id: string]: ICellMetric }
    private dctRefreshListByMetric: { [metricId: string]: string[] }
    private arrMetricViews: IMetricView[]
    private arrProfiles: WB_CubeSourcePerspectiveRow[]
    private dctDataSource: { [id: string]: ICubeSourceDataSource }

    private qsSyncCells: ExecutionSerializer
    private idxKeyOrder: string[]
    private idxIndexers: ((t: CubeSourceCell) => string)[]
    private idxCubeSourceCell: Index<CubeSourceCell>


    constructor(options?: ICubeSourceCreateOptions) {

        this.createOptions = _.defaultsDeep({}, options, { DimensionPolicyOptions: {} })
        this.createOptions.DimensionPolicyOptions.CubeSource = this

        this.cubeSourceOnLoadCompleteProgressProvider = new ProgressProvider()
        this.cubeSourceDataRowSyncProgressProvider = new ProgressProvider()
        this.cubeSourceLoadProgressProvider = new ProgressProviderMultiplexor()
        this.cubeSourceLoadProgressProvider.addSource(this.cubeSourceOnLoadCompleteProgressProvider)
        this.cubeSourceLoadProgressProvider.addSource(this.cubeSourceDataRowSyncProgressProvider)

        this.cubeSourceLoadMessageProvider = new MessageProvider()
        this.bCubeSourceLoaded = false
        this.onICubeSourceLoadComplete = undefined
        if (_.isBoolean(this.createOptions.Enabled))
            this.enabled = this.createOptions.Enabled

        this.cubeSourceRow = undefined
        this.dctParameters = {}
        this.onParameterChanged = new BehaviorSubject<ICubeSourceParameterChangeEvent>(undefined)
        this.onCubeSourcePropertyChange = new BehaviorSubject<ICubeSourceChangeEvent>(undefined)
        this.onCubeSourceMetricChange = new BehaviorSubject<ICubeSourceMetricChangeEvent>(undefined)

        this.metricRows = []
        this.metricViewRows = []
        this.dctHierarchy = {}
        this.dctHierarchyProps = {}

        this.dctMetrics_DataSource = {}
        this.dctMetricCellUOMs_Base = {}
        this.dctMetrics_Metrics = {}
        this.dctRefreshListByMetric = {}
        this.dctMetricConverters = {}
        this.arrMetricViews = []
        this.arrProfiles = []

        this.dctDataSource = {}

        this.qsSyncCells = new ExecutionSerializer(this, this.SyncCellsFromDataSources, [], 500)

        this.idxKeyOrder = []
        this.idxIndexers = []
        this.idxCubeSourceCell = undefined

        _.forEach(this.createOptions.Parameters, (value, key) => {
            this.setParameter(key, value)
        })

    }


    get CubeSourceName(): string {
        return this.cubeSourceRow?.cif?.CUBESOURCE_NAME || "Not Set"
    }


    get bICubeSourceLoaded(): boolean {
        return this.bCubeSourceLoaded
    }

    get OnICubeSourceLoadComplete(): Promise<void> {
        const self = this

        if (this.bCubeSourceLoaded)
            return Promise.resolve()

        if (!_.isUndefined(this.onICubeSourceLoadComplete))
            return this.onICubeSourceLoadComplete

        const returnValue = this.onICubeSourceLoadComplete = (async () => {
            try {
                //--
                self.cubeSourceOnLoadCompleteProgressProvider.start()
                let csmds = new CubeSourceMetaDataService()
                if (_.isNumber(self.createOptions?.WbCubeSourceId) && (self.createOptions?.WbCubeSourceId > 0))
                    self.cubeSourceRow = await csmds.getCubeSource(self.createOptions?.WbCubeSourceId)
                else if (!_.isEmpty(self.createOptions?.CubeSourceId))
                    self.cubeSourceRow = await csmds.getCubeSourceByID(self.createOptions?.CubeSourceId)

                if (_.isEmpty(self.cubeSourceRow)) {
                    self.cubeSourceOnLoadCompleteProgressProvider.exception("CubeSource not found")
                    return
                }

                self.metricRows = await csmds.getCubeSourceMetrics(self.cubeSourceRow.cif.WB_CUBESOURCE_ID)
                self.metricViewRows = await csmds.getCubeSourceMetricViews(self.cubeSourceRow.cif.WB_CUBESOURCE_ID)

                _.forEach(self.metricViewRows, (aRow) => {
                    if (!aRow.isActive)
                        return

                    let aMV: IMetricView = {
                        MetricViewId: aRow.cif.METRIC_VIEW_ID,
                        MetricViewName: aRow.cif.METRIC_VIEW_NAME,
                        MetricIds: [],
                        Sort: aRow.cif.SORT_ORDER
                    }

                    let arrMetricRel = _.filter(aRow.WB_MetricViewMetricRows, (a) => a.isActive && !_.isEmpty(a.MetricId))
                    arrMetricRel = _.sortBy(arrMetricRel, ['SORT_ORDER', 'MetricName'])
                    aMV.MetricIds = _.map(arrMetricRel, (a) => a.MetricId)
                    this.arrMetricViews.push(aMV)
                })
                this.arrMetricViews = _.sortBy(this.arrMetricViews, ['Sort', 'MetricViewName'])

                this.arrProfiles = await csmds.getCubeSourceProfiles(self.cubeSourceRow.cif.WB_CUBESOURCE_ID)

                //-- Load Hierarchy Policies
                let dctPolicies = csmds.getCubeSourceHierarchyPolicies()

                //-- Create Dimension Hierarchies using Policies
                let dimOptions = _.clone(self.createOptions.DimensionPolicyOptions)
                dimOptions.CubeSource = self
                let dimProperties: { [dimType: string]: { LeafLevel: string } } = {}
                let dmLoadTasks: Promise<any>[] = []
                for (let aRow of self.cubeSourceRow.WB_CubeSourceDimensionRows) {
                    if (!aRow.isActive)
                        continue

                    let dimType = <DimensionType>aRow.cif.DIMENSION_TYPE
                    let policyId = aRow.cif.HIERARCHY_POLICY
                    let policy = dctPolicies[policyId]
                    if (_.isUndefined(policy)) {
                        if (policyId != 'NamedParameterOnly')
                            self.cubeSourceLoadMessageProvider.addTextMessages(`NoPolicy-${dimType}`, MessageType.Error,
                                [`Policy (${policyId}) not found for Dimension (${dimType})`])
                        continue
                    }

                    let hierProps = await policy.getHierarchyProperties(dimType, dimOptions)
                    if (hierProps?.bSkip) {
                        continue
                    }

                    self.idxKeyOrder.push(aRow.cif.DIMENSION_TYPE)
                    self.idxIndexers.push((csC) => csC.getKey(dimType))


                    if (_.isEmpty(hierProps)) {
                        self.cubeSourceLoadMessageProvider.addTextMessages(
                            `NoHierProps-${dimType}`,
                            MessageType.Error,
                            [`Policy (${policyId}) couldn't determine hierarchy properties for Dimension (${dimType})`]
                        );
                        continue
                    }
                    dimProperties[dimType] = { LeafLevel: hierProps.LeafLevel };

                    let csHierarchy: IHierarchy = hierProps.Hierarchy

                    if (_.isEmpty(csHierarchy)) {
                        switch (dimType) {

                            case DimensionType.Customer:
                                csHierarchy = new CustomerHierarchy(+hierProps.HierarchyId, hierProps.EffectiveStartDt, hierProps.EffectiveEndDt, _.defaults({}, hierProps.HierarchyCreateOptions));
                                break;

                            case DimensionType.Product:
                                csHierarchy = new ProductHierarchy(+hierProps.HierarchyId, hierProps.EffectiveStartDt, hierProps.EffectiveEndDt, ), _.defaults({}, { bExcludeProducts: true }, hierProps.HierarchyCreateOptions);
                                break;

                            case DimensionType.Time:
                                csHierarchy = new PeriodHierarchy(+hierProps.HierarchyId, _.defaults({}, hierProps.HierarchyCreateOptions));
                                break;
                            case DimensionType.TimeSecondary:
                                csHierarchy = new PeriodHierarchy(+hierProps.HierarchyId, _.defaults({}, { dimType: DimensionType.TimeSecondary }, hierProps.HierarchyCreateOptions));
                                break;

                        }
                    }

                    if (_.isEmpty(csHierarchy)) {
                        self.cubeSourceLoadMessageProvider.addTextMessages(`NoHier-${dimType}`, MessageType.Error,
                            [`Couldn't Create hierarchy ${JSON.stringify(hierProps)} for Dimension (${dimType})`])
                        continue
                    }
                    let csHierarchyOrg = csHierarchy
                    if(!_.isEmpty(hierProps.HierarchyFilterOptions)){
                        // Create Filtered Hierarchy
                        csHierarchy = new FilteredHierarchy(csHierarchyOrg, hierProps.HierarchyFilterOptions)

                    }

                    dmLoadTasks.push(csHierarchy.OnLoadComplete)
                    self.dctHierarchy[dimType] = csHierarchy
                    self.dctHierarchyProps[dimType] = hierProps
                }

                await Promise.all(dmLoadTasks)

                // Check if all hierarchies are Loaded

                // Add RowIndex
                self.idxCubeSourceCell = new Index<CubeSourceCell>(self.idxIndexers)

                //-- Create DataSources
                let dsLoadTasks: Promise<any>[] = []
                for (let aRow of self.cubeSourceRow.WB_CubeSourceDataSourceRows) {
                    if (!aRow.isActive)
                        continue

                    if (_.isEmpty(aRow.WB_DataObjectRow))
                        continue

                    let ds: ICubeSourceDataSource = undefined
                    let aliasId = aRow.cif.ALIAS_ID
                    let dsType = aRow.WB_DataObjectRow.cif.DATAOBJECT_TYPE

                    switch (dsType) {
                        case "Table": {
                            const dsJSD = new DataSourceJSData(self, aRow, {
                                dbContext: self.createOptions.dbContext,
                                LoadOptions: (self.createOptions.LoadOptions[aliasId] || {}),
                                KnownKeyOptions: (self.createOptions.KnownKeyOptions[aliasId] || {}),
                                DimensionProperties: dimProperties,
                                RecordResolverOptions: (self.createOptions?.DataSourceOptions?.[aliasId]?.RecordResolverOptions || {})
                            })
                            ds = dsJSD
                            break;
                        }
                        default:
                            self.cubeSourceLoadMessageProvider.addTextMessages(`DSType-${dsType}`, MessageType.Error,
                                [`DataSource DataObject type (${dsType}) not supported yet`])
                            continue // Next DataSource

                    }
                    self.cubeSourceLoadProgressProvider.addSource(ds.LoadProgressProvider)

                    //--
                    self.dctDataSource[ds.Alias] = ds
                    dsLoadTasks.push(ds.OnLoadComplete)

                    // Listen for Row Changes
                    ds.OnRowsChanged.subscribe(self.evtOnDataSourceRowChanged.bind(self))

                } // Next DataSource

                await Promise.all(dsLoadTasks)

                // Check if all data sources are Loaded
                // Create Data Source Metrics
                for (let aDS of _.values(self.dctDataSource)) {
                    if (!aDS.bIsLoaded) {
                        continue
                    }

                    let dsMetrics = await aDS.getColumnMetrics(self.createOptions.MetricPolicyOptions || {})
                    for (let metric of dsMetrics) {
                        self.dctMetrics_DataSource[metric.MetricId] = metric
                        let uom = metric.getMetricUOM(self.createOptions.UOMPolicyOptions)
                        // @ts-ignore
                        if (_.isFunction(uom?.then))
                            uom = await uom
                        self.dctMetricCellUOMs_Base[metric.MetricId] = <ICellMetricUOM>uom
                    }

                }

                // Create CubeSource Metric
                //CubeSourceMetricDefinition
                for (let aMetricDfn of self.metricRows) {
                    let am = new CubeSourceMetricDefinition(aMetricDfn)
                    self.dctMetrics_Metrics[am.MetricId] = am

                    // Add UOMs for Metric Definitions
                    let uom = am.getMetricUOM(self.createOptions.UOMPolicyOptions)
                    // @ts-ignore
                    if (_.isFunction(uom?.then))
                        uom = await uom
                    self.dctMetricCellUOMs_Base[am.MetricId] = <ICellMetricUOM>uom
                }

                // Get distinct metric Converters, from DataSource Metrics
                for (let dsMetric of _.values(self.dctMetrics_DataSource)) {
                    // @ts-ignore
                    let mCnvtr: ICellMetricUOMConverters = dsMetric.getMetricUOMConverters()
                    // @ts-ignore
                    if (!_.isEmpty(mCnvtr?.NumeratorUOMConverters)) {
                        for (let cnvtrId of mCnvtr.NumeratorUOMConverters) {
                            if (!(cnvtrId in self.dctMetricConverters))
                                self.dctMetricConverters[cnvtrId] = await UOMConverterService.getConverter(cnvtrId, self.createOptions.UOMConverterOptions)
                        }
                    }
                    if (!_.isEmpty(mCnvtr?.DenominatorUOMConverter)) {
                        for (let cnvtrId of mCnvtr.DenominatorUOMConverter) {
                            if (!(cnvtrId in self.dctMetricConverters))
                                self.dctMetricConverters[cnvtrId] = await UOMConverterService.getConverter(cnvtrId, self.createOptions.UOMConverterOptions)
                        }
                    }
                }

                // Create Metric RefreshList
                self.dctRefreshListByMetric = CellMetricUtils.getRefreshMetricParentsByMetricId(self)

                self.bCubeSourceLoaded = true
                self.cubeSourceOnLoadCompleteProgressProvider.finish()
            } catch (err) {
                console.log("OnICubeSourceLoadComplete error: ", err)
                self.cubeSourceOnLoadCompleteProgressProvider.exception(err)
                throw err
            }

        })();

        return returnValue
    }

    get CubeSourceLoadMessageProvider(): IMessageProvider {
        return this.cubeSourceLoadMessageProvider
    }

    get CubeSourceLoadProgressProvider(): IProgressProvider {
        return this.cubeSourceLoadProgressProvider
    }

    createPerspective(name: string, perspectiveOptions: ICubePerspectiveOptions): ICubePerspective {
        const self = this

        let cellFunction = async () => {
            //-- Add code to get limited dataset
            // create scope Object
            let scope: IRowScope = {}
            _.forEach(perspectiveOptions.DimensionFilters || {}, (dimFilterProps, dimType) => {
                if ('FilterByNodes' in dimFilterProps) {
                    scope[dimType] = {
                        bAll: false,
                        Hierarchy: self.getDimensionHierarchy(<DimensionType>dimType),
                        HierarchyNodes: dimFilterProps.FilterByNodes || []
                    }
                } else {
                    scope[dimType] = {
                        bAll: true,
                        Hierarchy: self.getDimensionHierarchy(<DimensionType>dimType),
                        HierarchyNodes: []
                    }
                }
            })

            for (let ds of _.values(self.dctDataSource)) {
                let dsRows = await ds.getRowScope(scope)
            }

            return self.getCellScope(scope)
        };

        if (!this.enabled)
            perspectiveOptions.bEnabled = false

        return new Perspective(name, this, perspectiveOptions, {
            cells: cellFunction,
            cacheCellMetricUOMs: self.dctMetricCellUOMs_Base,
            uomPolicyOptions: self.createOptions.UOMPolicyOptions
        })
    }

    get OnCubeSourceMetricChange(): BehaviorSubject<ICubeSourceMetricChangeEvent> {
        return this.onCubeSourceMetricChange
    }

    // Cube Source Metadata
    getDimensions(): DimensionType[] {
        return <DimensionType[]>_.keys(this.dctHierarchy);
    }

    getDimensionHierarchy(dimensionType: DimensionType): IHierarchy {
        return this.dctHierarchy[dimensionType];
    }

    getDimensionLeafLevels(): { [dimType: string]: string } {
        let rv: { [dimType: string]: string } = {}
        _.forEach(this.dctHierarchyProps, (aDP, dimType) => {
            if (!_.isEmpty(aDP?.LeafLevel))
                rv[dimType] = aDP.LeafLevel
        })
        return rv;
    }

    // Gets all the Metrics that should be visible in the UI
    getMetrics(options?: { bIncludeWriteBack?: boolean }): ICellMetric[] {
        let bIncludeWriteBack = !!(options?.bIncludeWriteBack)

        return _.filter(_.union(_.values(this.dctMetrics_Metrics),
                _.values(this.dctMetrics_DataSource)),
            (a) => {
                if (a.bIsLeafLevelOnly)
                    return false

                if (bIncludeWriteBack
                    && (a.bIsWriteBackEnabled || (a.RefreshMetricIdsOnWrite && a.RefreshMetricIdsOnWrite?.length)))
                    return true

                return !(a.bIsHidden)
            });
    }

    // Gets any metric, including dependent metrics
    getMetric(metricId: string): ICellMetric {
        if (metricId in this.dctMetrics_DataSource)
            return this.dctMetrics_DataSource[metricId];

        if (metricId in this.dctMetrics_Metrics)
            return this.dctMetrics_Metrics[metricId];

        return undefined
    }

    getCellMetricBaseUOM(metricId: string): ICellMetricUOM {
        return this.dctMetricCellUOMs_Base[metricId];
    }

    getMetricUOMConverter(uomConverterId: string): IUOMConverter {
        return this.dctMetricConverters[uomConverterId];
    }

    getRefreshMetricIds(aMetricId: string): string[] {
        return (this.dctRefreshListByMetric[aMetricId] || [aMetricId]);
    }

    getMetricViews(): IMetricView[] {
        return this.arrMetricViews;
    }


    getProfiles(): WB_CubeSourcePerspectiveRow[] {
        return this.arrProfiles
    }

    addCubeProfile(options: ICubeProfileCreateOptions): Promise<WB_CubeSourcePerspectiveRow> {
        const self = this
        return new Promise<WB_CubeSourcePerspectiveRow>(async (resolve, error) => {
            let dbContext = self.createOptions.dbContext
            let newRow = <WB_CubeSourcePerspectiveRow>await dbContext.createRecordWithKey(esWB_CubeSource_Perspective)
            newRow.setDefaults()
            newRow.cif.WB_CUBESOURCE_ID = self.cubeSourceRow.cif.WB_CUBESOURCE_ID
            newRow.cif.VIEW_ID = "New Profile"
            newRow.cif.VIEW_NAME = "New Profile"
            newRow.cif.SOURCE_WB_ID = options.sourceWbId
            newRow.cif.SOURCE_TYPE = options.sourceType
            newRow.cif.SOURCE_SUBTYPE = options.sourceSubType
            newRow.cif.VIEW_TYPE = options.viewType
            self.arrProfiles.push(newRow)
            resolve(newRow)
        })
    }

    removeCubeProfile(profileWbId: number): void {
        const self = this
        let row = _.first(_.filter(self.arrProfiles, p => p.cif.WB_CUBESOURCEPVR_ID !== profileWbId))
        if (row)
            row.markDeleted()

    }

    saveCubeProfiles(profiles: WB_CubeSourcePerspectiveRow[]): Promise<void> {
        const self = this


        return new Promise<void>(async (resolve, error) => {
            try {
                let dbContext = new PAdbContext()
                await dbContext.Save(esWB_CubeSource_Perspective, profiles)
                resolve()
            } catch (e) {
                error(e)
            }
        })
    }

    // Source Leaf Cell retrieval
    // -- perspectives will utilize these methods, -- these could be over the wire, so async
    async getDistinctLeafKeys(dimensionTypeIds: DimensionType[], options: IKeyOptions): Promise<IDimensionDistinctKeys> {
        let arr: IDimensionDistinctKeys[] = []

        for (let ds of _.values(this.dctDataSource)) {
            let keys = await ds.getDistinctKeys(dimensionTypeIds, options)
            if (_.isEmpty(keys))
                continue
            arr.push(keys)
        }

        if (!arr.length)
            return {}

        if (arr.length == 1)
            return arr[0]

        let rvd: { [dimId: string]: { [idx: string]: number | string } } = {}

        for (let item of arr) {
            _.forEach(item, (arrValues, key) => {
                if (!(key in rvd))
                    rvd[key] = {}
                _.forEach(arrValues, (a) => {
                    rvd[key][a] = a
                })
            })
        }

        let rv: IDimensionDistinctKeys = {}
        _.forEach(rvd, (dct, key) => {
            rv[key] = <any>_.values(dct)
        })

        return rv
    }

    async getCompositeLeafKeys(dimensionTypeIds: DimensionType[], options: IKeyOptions): Promise<IDimensionCompositeKeys> {
        //-- TODO
        return {}
    }

    get OnCubeSourcePropertyChange(): BehaviorSubject<ICubeSourceChangeEvent> {
        return this.onCubeSourcePropertyChange
    }

    //-- ICubeSourceWithDataSources

    // Parameters
    getParameter(pName: string): (number | string)[] {
        if (_.isEmpty(pName))
            return []

        return this.dctParameters[pName] || [];
    }

    setParameter(pName: string, value: (number | string)[] | number | string): void {
        if (_.isEmpty(pName))
            return
        if (_.isUndefined(value) || _.isEmpty(value))
            this.dctParameters[pName] = []
        else if (_.isArray(value))
            this.dctParameters[pName] = value
        else
            this.dctParameters[pName] = [value]

        this.OnParameterChanged.next({
            CubeSource: this,
            Name: pName,
            Value: this.dctParameters[pName]
        })
    }

    get OnParameterChanged(): BehaviorSubject<ICubeSourceParameterChangeEvent> {
        return this.onParameterChanged
    }

    setEnabled(pEnabled: boolean): void {
        this.enabled = pEnabled
    }

    get bEnabled(): boolean {
        return this.enabled
    }


    // DataSource
    getDataSource(sAlias: string): ICubeSourceDataSource {
        return this.dctDataSource[sAlias]
    }

    get DataSources(): ICubeSourceDataSource[] {
        return _.values(this.dctDataSource)
    }

    // Invalidate Loaded Rows
    purgeScope(scope: IRowScope, sAlias?: string | string[], options?: { bSkipPurgeContext?: boolean }): void {
        let aliases: string[] = []
        if (_.isUndefined(sAlias))
            aliases = _.keys(this.dctDataSource)
        else if (_.isString(sAlias))
            aliases = [sAlias]
        else if (_.isArray(sAlias))
            aliases = sAlias
        _.forEach(aliases, (a) => {
            let ds = this.getDataSource(a)
            if (ds)
                ds.purgeScope(scope, options)
        })
    }

    //
    // Sync Cell
    private evtOnDataSourceRowChanged(evt: IDataSourceOnRowsChangedEvent) {
        if (_.isEmpty(evt))
            return

        this.qsSyncCells.Run({ sReason: `DataSource Change: ${evt.DataSource.Alias}` })
    }

    private async SyncCellsFromDataSources(): Promise<void> {
        const self = this

        if (!this.bCubeSourceLoaded)
            return

        // Sync Algorithm,
        // A. Mark all the records with Integration Indicator = true
        // B. Update all cells with loaded rows from DataSources, setting Integration Indicator = false
        // C. Purge all records where Integration Indicator = true

        try {
            self.cubeSourceDataRowSyncProgressProvider.start("Sync Sources")
            // A. Mark all the records with Integration Indicator = true
            self.idxCubeSourceCell.forEach((a: CubeSourceCell) => a.setIntegrationMarkers())


            // B. Add/ Update all cells with loaded rows from DataSources, setting Integration Indicator = false
            for (let ds of _.values(self.dctDataSource)) {
                let arrKeyFieldNames = _.map(self.idxKeyOrder, (dimType) => ds.getRowDimensionKeyField(<any>dimType))
                let rows = ds.LoadedRows
                for (let aRow of rows) {
                    let arrRowKeys = _.map(arrKeyFieldNames, (keyField) => aRow[keyField])
                    let cell: CubeSourceCell = <CubeSourceCell>self.idxCubeSourceCell.get(arrRowKeys)
                    if (_.isEmpty(cell)) {
                        let cellId = arrRowKeys.join(':')
                        cell = new CubeSourceCell(self, cellId, self.idxKeyOrder, arrRowKeys)
                        // Set Metric UOMs
                        _.forEach(self.dctMetricCellUOMs_Base, (uom, metricId) => {
                            cell.setCellMetricUOM(metricId, uom)
                        })
                        self.idxCubeSourceCell.add(cell)
                    }
                    cell.setDataRow(ds.Alias, aRow)
                }
            }

            // C. Purge all records where Integration Indicator = true
            self.idxCubeSourceCell.forEach((a: CubeSourceCell) => a.purgeRowsWithIntegrationMarker())
            self.idxCubeSourceCell.remove((a: CubeSourceCell) => (a.rowCount() == 0))

            self.cubeSourceDataRowSyncProgressProvider.finish()

        } catch (err) {
            self.cubeSourceDataRowSyncProgressProvider.exception(err)
            ExceptionMessage_restate(self.CubeSourceLoadMessageProvider, err)
            console.log("SyncCellsFromDataSources: Err: ", err)
        }

        self.OnCubeSourcePropertyChange.next({ Source: self, PropertyName: "Perspective", PropertyValue: undefined })
    }

    private getCellScope(scope: IRowScope): CubeSourceCell[] {
        const self = this
        let bFilterAll = false
        let dctFilterKeys: { [dimType: string]: { [value: string | number]: string | number } } = {}

        _.forEach(this.dctHierarchy, (hier, dimType) => {

            // If in scope, check coverage, else all must be loaded
            if (!(dimType in scope))
                return

            let sc = scope[dimType]
            if (sc.bAll)
                return

            if (_.isEmpty(sc.HierarchyNodes)) {
                bFilterAll = true
                return false
            }


            if (_.isEmpty(hier)) {
                let msg = `getCellScope: Unknown Hierarchy ${dimType}`
                console.log(msg)
                bFilterAll = true
                return false
            }
            let hp = self.dctHierarchyProps[dimType]

            dctFilterKeys[dimType] = {}
            let dctKeys = dctFilterKeys[dimType]

            // If Filtered at Leaf Level, add to Filter
            let atLeafLevelNodes = _.filter(sc.HierarchyNodes, (a) => a.NodeLevel == hp.LeafLevel)
            _.forEach(atLeafLevelNodes, (aLeaf) => {
                dctKeys[aLeaf.NodeId] = aLeaf.NodeId
            })

            // Find All Leaf Nodes from any parent nodes and add to Filters
            if (_.isArray(hier)) {
                _.forEach(hier, (h) => {
                    let leafNodes = h.getChildren(sc.HierarchyNodes, hp.LeafLevel)
                    _.forEach(leafNodes, (aLeaf) => {
                        dctKeys[aLeaf.NodeId] = aLeaf.NodeId
                    })
                })
            } else {
                let leafNodes = hier.getChildren(sc.HierarchyNodes, hp.LeafLevel)
                _.forEach(leafNodes, (aLeaf) => {
                    dctKeys[aLeaf.NodeId] = aLeaf.NodeId
                })
            }
        })

        if (bFilterAll)
            return []

        // Run through filters
        let rv: CubeSourceCell[] = <CubeSourceCell[]>this.idxCubeSourceCell.values()
        _.forEach(dctFilterKeys, (dctKeys, dimType) => {
            rv = _.filter(rv, (aRow) => {
                return (aRow.getKey(dimType) in dctKeys)
            })
        })

        return rv
    }

}